import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useDeleteClub } from '../../../../../hooks/clubs/useDeleteClub'

import { ClubCard } from './clubCard/ClubCard'
import { ListingDetails } from '../../../../listing/ListingDetails'

import { CONFIG } from '../../../../../config'
import { useApp } from '../../../../../contexts/AppContext'
import { DisplayType } from '../../../../../types/listing'
import { ActionType } from '../../../../../constants/actionTypes'
import { useConfirm } from '../../../../../contexts/ConfirmContext'
import { ConfirmLayout } from '../../../../../types/confirm/confirm'
import { BannerKind } from '../../../../../types/contexts/AppContext'
import { ClubModelMetadata } from '../../../../../types/court/court'
import { displayHourMinute } from '../../../../../utils/dateUtils'

export const ClubList = () => {
  const navigate = useNavigate()
  const { showBanner } = useApp()
  const { confirm } = useConfirm()
  const { t } = useTranslation('clubs')
  const [refreshTrigger, setRefreshTrigger] = useState(false)

  const { deleteClub } = useDeleteClub()

  const handleDelete = (club: ClubModelMetadata) => {
    if (club.id === undefined) {
      return
    }

    confirm({
      layout: ConfirmLayout.DETAILS,
      title: t('clubs.delete-confirmation.title'),
      subtitle: t('clubs.delete-confirmation.subtitle'),
      confirmLabel: t('clubs.delete-confirmation.confirm'),
      backLabel: t('clubs.delete-confirmation.cancel'),
      returnTo: '/m/clubs',
      itemCards: [
        {
          id: club.id,
          title: club.name,
          details: [
            {
              label: t('clubs.card.courts'),
              value: t('clubs.card.courts-count', { count: club.courtsCount ?? 0 }),
            },
            {
              label: t('clubs.card.open-time'),
              value: displayHourMinute(club.openTime),
            },
            {
              label: t('clubs.card.close-time'),
              value: displayHourMinute(club.closeTime),
            },
            {
              label: t('clubs.card.free-cancellation'),
              value: t('clubs.card.hours', {
                count: club.freeCancellation,
              }),
            },
          ],
        },
      ],
      onConfirm: async () => {
        await deleteClub(club.id!)

        showBanner({
          kind: BannerKind.OK,
          msg: t('clubs.successfully-deleted'),
        })

        setRefreshTrigger(current => !current)
      },
    })
  }

  const handleAction = (club: ClubModelMetadata, action: ActionType) => {
    if (club.id === undefined) {
      return
    }

    switch (action) {
      case ActionType.EDIT:
        navigate(`/m/clubs/${club.id}`)
        return
      case ActionType.VIEW:
        navigate(`/m/courts/club/${club.id}`)
        return
      case ActionType.DELETE:
        handleDelete(club)
        return
    }
  }

  const renderClub = (club: ClubModelMetadata) => (
    <ClubCard club={club} onAction={handleAction} />
  )

  return (
    <ListingDetails<ClubModelMetadata>
      endpoint={`${CONFIG.BOUNCE_API}/api/v1/clubs`}
      initialViewMode={DisplayType.GRID}
      renderItem={renderClub}
      refreshTrigger={refreshTrigger}
      onError={message =>
        showBanner({
          kind: BannerKind.ERR,
          msg: message,
        })
      }
      gridItemClassName="col-12"
    />
  )
}
